import React, { FC, useState } from 'react';
import { Dropdown } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Shared UI Library Components
import { CustomIcon, EIconName, Loader } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './change-profile-status.scss';

// Store entities
import {
  activateCounsellor,
  blockCounsellor,
  deactivateCounsellor, deleteCounsellor, loadCounsellors, resendInvitationCounsellor, shareLinkCounsellor,
  unblockCounsellor
} from '../../../store/counsellors/counsellors.thunks';

// Components
import { BlockCounsellorModal } from '../../../modals/block-counsellor-modal/block-counsellor-modal';
import {
  ActivateCounsellorStripeModal
} from '../../../modals/activate-counsellor-stripe-modal/activate-counsellor-stripe-modal';

// Models
import { TCounsellorCompanyStatus } from '../../../types/counsellor.types';

type TModalKeys = 'invitation' | 'block' | 'deactivate' | 'delete';

type TChangeProfileStatusProps = {
  worker_company_status: TCounsellorCompanyStatus;
  counsellor_id: number;
  stripeConnected: boolean;
  canActivate: boolean;
  resetList?: () => void;
};

export const ChangeProfileStatus: FC<TChangeProfileStatusProps> = (
  {
    worker_company_status,
    counsellor_id,
    stripeConnected,
    canActivate,
    resetList = () => {},
  }: TChangeProfileStatusProps
) => {

  const [openSubmitModal, setOpenSubmitModal] = useState<TModalKeys | null>(null);
  const [openActivateStripeModal, setOpenActivateStripeModal] = useState<boolean>(false);
  const [changingStatus, setChangingStatus] = useState<boolean>(false);

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const modalData = {
    invitation: {
      title: 'Are you sure you want to resend invitation email to Consultant?',
      subtitle: 'Please Note: The Consultant will receive a new auto-generated password to logging into the system.',
    },
    block: {
      title: 'Are you sure you want to block the Consultant?',
      subtitle: '',
    },
    deactivate: {
      title: 'Are you sure you want to deactivate the Consultant?',
      subtitle: '',
    },
    delete: {
      title: 'Are you sure you want to delete the Consultant?',
      subtitle: 'Please Note: The Consultant cannot be deleted if there was any history.',
    }
  }[openSubmitModal || 'block'];

  const openModal = (type: TModalKeys): void => {
    setOpenSubmitModal(type);
  }

  const clearSubmitModal = (): void => {
    setOpenSubmitModal(null);
  }

  const toggleActivateStripeModal = (): void => {
    setOpenActivateStripeModal(!openActivateStripeModal);
  }

  const onActivateCounsellorSubmitHandler = (): void => {
    toggleActivateStripeModal();
    onActivateCounsellorHandler();
  }

  const onActivateStripeCheckingHandler = async (): Promise<void> => {
    if (!stripeConnected) {
      toggleActivateStripeModal();
      return;
    }
    onActivateCounsellorHandler();
  }

  const onActivateCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    await dispatch(activateCounsellor({ counsellor_id }));
    setChangingStatus(false);
  }

  const onDeactivateCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    await dispatch(deactivateCounsellor({ counsellor_id }));
    setChangingStatus(false);
  }

  const onDeleteCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    const { payload } = await dispatch(deleteCounsellor({ counsellor_id }));
    setChangingStatus(false);

    if (!payload) return;

    if (window.location.pathname === '/counsellors') {
      resetList();
      dispatch(loadCounsellors({ filters: { offset: 0 }, isFirstLoad: true }));
      return;
    }
    navigate('/counsellors');
  }

  const onBlockCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    await dispatch(blockCounsellor({ counsellor_id }));
    setChangingStatus(false);
  }

  const onUnblockCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    await dispatch(unblockCounsellor({ counsellor_id }));
    setChangingStatus(false);
  }

  const onShareLinkCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    await dispatch(shareLinkCounsellor({ counsellor_id }));
    setChangingStatus(false);
  }

  const onResendInvitationCounsellorHandler = async (): Promise<void> => {
    setChangingStatus(true);
    await dispatch(resendInvitationCounsellor({ counsellor_id }));
    setChangingStatus(false);
  }

  const onSubmitBlock = (): void => {
    if (openSubmitModal === 'block') {
      onBlockCounsellorHandler();
    }
    if (openSubmitModal === 'deactivate') {
      onDeactivateCounsellorHandler();
    }
    if (openSubmitModal === 'delete') {
      onDeleteCounsellorHandler();
    }
    if (openSubmitModal === 'invitation') {
      onResendInvitationCounsellorHandler();
    }
    setOpenSubmitModal(null);
  }

  const onActionHandler = (data: { key?: string }): void => {
    if (data?.key === 'activate') {
      onActivateStripeCheckingHandler();
      return;
    }
    if (data?.key === 'delete') {
      openModal('delete');
      return;
    }
    if (data?.key === 'deactivate') {
      openModal('deactivate');
      return;
    }
    if (data?.key === 'block') {
      openModal('block');
      return;
    }
    if (data?.key === 'unblock') {
      onUnblockCounsellorHandler();
      return;
    }
    if (data?.key === 'share-link') {
      onShareLinkCounsellorHandler();
      return;
    }
    if (data?.key === 'resend-invitation') {
      openModal('invitation');
      return;
    }
    onActivateStripeCheckingHandler();
  }

  const activeItems = [
    {
      key: 'resend-invitation',
      label: (
        <div className="row-actions--item">
          <CustomIcon name={ EIconName.Send } size={ 'size-lg' }/>
          Resend Invitation Email to Consultant
        </div>),
      onClick: onActionHandler
    },
    {
      key: 'share-link',
      label: (
        <div className="row-actions--item">
          <CustomIcon name={ EIconName.Share } size={ 'size-lg' }/>
          Send Consultant App Link via Email
        </div>),
      onClick: onActionHandler
    },
    ...(worker_company_status === 'deactivated' ? [
      {
        key: 'activate',
        label: (
          <div className="row-actions--item">
            <CustomIcon name={ EIconName.CheckCircle } size={ 'size-lg' }/>
            Activate Consultant
          </div>),
        onClick: onActionHandler,
        disabled: !canActivate
      },
      {
        key: 'block',
        label: (
          <div className="row-actions--item">
            <CustomIcon name={ EIconName.ArchiveFill } size={ 'size-lg' }/>
            Block Consultant
          </div>),
        onClick: onActionHandler
      },
    ] : []),
    ...(worker_company_status === 'active' ? [{
      key: 'deactivate',
      label: (
        <div className="row-actions--item">
          <CustomIcon name={ EIconName.Decline } size={ 'size-lg' }/>
          Deactivate Consultant
        </div>),
      onClick: onActionHandler
    }] : []),
    ...(worker_company_status === 'blocked' ? [{
      key: 'unblock',
      label: (
        <div className="row-actions--item">
          <CustomIcon name={ EIconName.Decline } size={ 'size-lg' }/>
          Unlock Consultant
        </div>),
      onClick: onActionHandler
    }] : []),
    {
      key: 'delete',
      label: (
        <div className="row-actions--item delete-action">
          <CustomIcon name={ EIconName.Delete } size={ 'size-lg' }/>
          Delete Consultant
        </div>),
      onClick: onActionHandler
    },
  ];

  return (
    <div className="change-profile-status">
      <BlockCounsellorModal
        open={ openSubmitModal !== null }
        title={ modalData.title }
        subtitle={ modalData.subtitle }
        onCancel={ clearSubmitModal }
        onSubmit={ onSubmitBlock }
      />
      <ActivateCounsellorStripeModal open={ openActivateStripeModal } onCancel={ toggleActivateStripeModal }
                                     onSubmit={ onActivateCounsellorSubmitHandler }/>
      { !changingStatus ? <Dropdown menu={ {
          items: activeItems
        } } trigger={ ['click'] } placement="bottomRight" arrow>
          <CustomIcon name={ EIconName.BurgerMenu }/>
        </Dropdown>
        :
        <Loader/>
      }
    </div>
  );
}
